import { getHttpClient } from '@/services/httpClientProvider'
import { useAuthStore } from '@/stores/auth'
import type {
  CommParticipant,
  CommData,
  ContactData,
  GetCommParams,
  GetCommResponse,
  UpdateCommPresenceParams,
  UpdateCommPresenceResponse
} from '@/modules/inbox/types/communications'

// Email sending types
export interface EmailReplyData {
  to: string;
  toLabel: string;
  cc?: string[];
  subject: string;
  replyBody: string;
  from: string;
  from_name: string;
  from_email: string;
  outbound_from_integration_id?: string;
  origParts?: any[];
}

export interface SendEmailParams {
  id: string; // Communication ID
  commId: string;
  from_email: string;
  replyBody: string;
  subject: string;
  reply: EmailReplyData;
  files?: File[];
  file_tag?: string;
}

export interface SendEmailResponse {
  success: boolean;
  payloads?: Array<{
    id?: string;
    timestamp: string;
    channel: string;
    message: string;
    ttl?: any;
    context?: {
      from: string;
      from_name: string;
      from_nickname?: string;
      from_user?: any;
      from_avatar?: string;
      to: string;
      to_name: string;
      to_nickname?: string;
      to_user?: any;
      to_avatar?: string;
    };
    email?: {
      cc?: string;
      bcc?: string;
      subject: string;
      message_id?: string;
      body: string;
    };
    attachment?: any;
    kb?: any;
    revision?: any;
    actions?: any[];
    secret?: string;
    notificationsEnable?: boolean;
  }>;
  message?: string;
  current_server_time?: string;
  [key: string]: any;
}

// Communication label update types
export interface UpdateCommLabelParams {
  id: string;
  comm_label: string;
}

export interface UpdateCommLabelResponse {
  success: boolean;
  comm: CommData;
}

// Chat file upload types
export interface PutFileParams {
  id: string; // Communication ID
  file_tag: string;
  message: string;
  files: File[];
}

export interface PutFileAttachment {
  name: string;
  size: number;
  type: string;
  id: string;
  preview: string;
  url: string;
}

export interface PutFilePayload {
  timestamp: string;
  ttl: null;
  message: string;
  context: {
    from: string;
    from_name: string;
    from_nickname: string;
    from_avatar: string;
    to: string;
    to_name: string;
    to_nickname: string;
    to_user: null;
    to_avatar: string;
  };
  attachment: PutFileAttachment;
  email: Record<string, unknown>;
  kb: null;
  revision: null;
  actions: any[];
  secret: string;
  channel: string;
  notificationsEnable: boolean;
}

export interface PutFileResponse {
  success: boolean;
  payloads: PutFilePayload[];
  current_server_time: string;
}

export type CommDraftVisibility = 'private' | 'shared'

export interface CommDraft {
  id: string
  comm_id: string
  participant: string
  participant_id: string
  visibility: CommDraftVisibility
  secret: string
  payload: object
  mentions: string[]
  data: object
  created: string
  updated: string
}

export interface GetLatestDraftResponse {
  success?: boolean
  result: CommDraft | null
  message?: string
  errorType?: string
}

export interface SaveDraftResponse {
  success?: boolean
  result?: CommDraft
  message?: string
  errorType?: string
}

/** md5('plain') — chat composer payload type */
export const COMM_DRAFT_SECRET_CHAT = 'ac7938d40cfc2307e2bf325d28e7884e'
/** md5('email_message_known') — email composer payload type */
export const COMM_DRAFT_SECRET_EMAIL = '4ddae1c8d316f8199fd9f4c6f71a3203'
/** md5('sms') — SMS composer payload type */
export const COMM_DRAFT_SECRET_SMS = '18b43c6a536a8fe1362f7a3887936be6'

export function useCommsAPI() {
  const authStore = useAuthStore()
  const httpClient = getHttpClient(authStore)

  return {
    /**
     * Get communication details including external_id (XMPP room JID)
     */
    async getComm(params: GetCommParams): Promise<CommData> {
      try {
        const queryParams = {
          sAction: 'itemGet',
          id: params.id,
          ...(params.is_enter && { is_enter: 'true' })
        }

        const response = await httpClient.get<GetCommResponse>('/admin/v4/comm/', queryParams)
        
        if (!response.success) {
          throw new Error(`Failed to get communication: ${response}`)
        }

        return response.comm
      } catch (error) {
        console.error('Error fetching communication:', error)
        throw error
      }
    },

    async getCommTranscripts(commId: string): Promise<any[]> {
      const response = await httpClient.get<{ success?: boolean; transcripts?: any[] }>('/admin/v4/comm/', {
        sAction: 'getTranscripts',
        id: commId,
      })
      return response.transcripts || []
    },

    async getCommFiles(commId: string): Promise<any[]> {
      const response = await httpClient.get<{ upload_files?: any[] }>('/admin/v4/comm/', {
        sAction: 'getFiles',
        id: commId,
      })
      return response.upload_files || []
    },

    /**
     * Update communication presence (for leaving rooms)
     */
    async updateCommPresence(params: UpdateCommPresenceParams): Promise<CommData> {
      try {
        const formData = new URLSearchParams()
        formData.append('comm', JSON.stringify([{
          presence: params.presence,
          id: params.id
        }]))
        formData.append('stringify', 'false')

        const response = await httpClient.post<UpdateCommPresenceResponse>(
          '/admin/v4/comm/',
          formData,
          { sAction: 'itemPut' },
          { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' }
        )
        
        if (!response.success) {
          throw new Error(`Failed to update communication presence: ${response}`)
        }

        return response.comm
      } catch (error) {
        console.error('Error updating communication presence:', error)
        throw error
      }
    },

    /**
     * Update communication label
     */
    async updateCommLabel(params: UpdateCommLabelParams): Promise<CommData> {
      try {
        const formData = new URLSearchParams()
        formData.append('comm', JSON.stringify([{
          id: params.id,
          comm_label: params.comm_label
        }]))
        formData.append('stringify', 'false')

        const response = await httpClient.post<UpdateCommLabelResponse>(
          '/admin/v4/comm/',
          formData,
          { sAction: 'itemPut' },
          { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' }
        )
        
        if (!response.success) {
          throw new Error(`Failed to update communication label: ${response}`)
        }

        return response.comm
      } catch (error) {
        console.error('Error updating communication label:', error)
        throw error
      }
    },

    /**
     * Send an email with optional attachments
     */
    async sendEmail(params: SendEmailParams): Promise<SendEmailResponse> {
      try {
        // Try using URLSearchParams like other successful API calls
        const payload = new URLSearchParams()
        
        // Add required fields
        payload.append('commId', params.commId)
        payload.append('reply', JSON.stringify(params.reply))
        
        // Add stringify false like other POST methods
        payload.append('stringify', 'false')
        
        // Add optional file tag
        if (params.file_tag) {
          payload.append('file_tag', params.file_tag)
        }
        
        // Note: Files are not supported with URLSearchParams format
        // If files are needed, we'll need to use FormData, but let's try this first
        if (params.files && params.files.length > 0) {
          
          // Fall back to FormData if files are present
          const formData = new FormData()
          formData.append('commId', params.commId)
          formData.append('reply', JSON.stringify(params.reply))
          formData.append('stringify', 'false')
          
          if (params.file_tag) {
            formData.append('file_tag', params.file_tag)
          }
          
          params.files.forEach((file, index) => {
            formData.append(`file_${index}`, file)
          })

          const response = await httpClient.post<SendEmailResponse>(
            '/admin/v4/comm/',
            formData,
            { sAction: 'rep' },
            { 'content-type': 'multipart/form-data' }
          )
          
        if (!response.success) {
          throw new Error(
            typeof response.message === 'string' && response.message.trim()
              ? response.message
              : 'Failed to send email'
          )
        }

        return response
        }

        const response = await httpClient.post<SendEmailResponse>(
          '/admin/v4/comm/',
          payload,
          { sAction: 'rep' },
          { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' }
        )
        
        if (!response.success) {
          throw new Error(
            typeof response.message === 'string' && response.message.trim()
              ? response.message
              : 'Failed to send email'
          )
        }

        return response
      } catch (error) {
        console.error('Error sending email:', error)
        throw error
      }
    },

    /**
     * Upload files for chat communication
     */
    async putFile(params: PutFileParams): Promise<PutFileResponse> {
      try {
        const formData = new FormData()
        
        // Add required fields
        formData.append('id', params.id)
        formData.append('file_tag', params.file_tag)
        formData.append('message', params.message)
        
        // Add files with indexed naming
        params.files.forEach((file, index) => {
          formData.append(`file_${index}`, file)
        })
        
        // Add fileId array for each file
        params.files.forEach((file) => {
          formData.append('fileId[]', file.name)
        })

        const response = await httpClient.post<PutFileResponse>(
          '/admin/v4/comm/',
          formData,
          { sAction: 'putFile' },
          { 'content-type': 'multipart/form-data' }
        )
        
        if (!response.success) {
          throw new Error(`Failed to upload files: ${response}`)
        }

        return response
      } catch (error) {
        console.error('Error uploading files:', error)
        throw error
      }
    },

    /**
     * Get the latest comm draft for a comm and visibility (private or shared).
     */
    async getLatestDraft(commId: string, visibility?: CommDraftVisibility): Promise<CommDraft | null> {
      try {
        const queryParams: Record<string, string> = {
          sAction: 'getLatestDraft',
          comm_id: commId,
        }
        if (visibility) {
          queryParams.visibility = visibility
        }

        const response = await httpClient.get<GetLatestDraftResponse>(
          '/admin/v4/comm/',
          queryParams
        )

        if (response.success === false) {
          throw new Error(response.message || 'Failed to fetch comm draft')
        }

        return response.result ?? null
      } catch (error) {
        console.error('Error fetching comm draft:', error)
        throw error
      }
    },

    /**
     * Create or update a comm draft. Supply id on subsequent auto-saves for the same draft.
     */
    async saveDraft(
      commId: string,
      secret: string,
      payload: object,
      id?: string,
      mentions?: string[],
      data?: object,
      visibility?: CommDraftVisibility,
      options?: { signal?: AbortSignal }
    ): Promise<CommDraft> {
      try {
        const formData = new URLSearchParams()
        formData.append('secret', secret)
        formData.append('payload', JSON.stringify(payload))
        formData.append('stringify', 'false')

        if (id) {
          formData.append('id', id)
        } else {
          formData.append('comm_id', commId)
        }
        if (mentions?.length) {
          mentions.forEach((mention) => formData.append('mentions[]', mention))
        }
        if (data && Object.keys(data).length > 0) {
          formData.append('data', JSON.stringify(data))
        }
        if (visibility) {
          formData.append('visibility', visibility)
        }

        const response = await httpClient.post<SaveDraftResponse>(
          '/admin/v4/comm/',
          formData,
          { sAction: 'putDraft' },
          { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' },
          undefined,
          options?.signal
        )

        if (response.success === false) {
          throw new Error(response.message || 'Failed to save comm draft')
        }

        if (!response.result) {
          throw new Error('Failed to save comm draft: empty result')
        }

        return response.result
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          throw error
        }
        console.error('Error saving comm draft:', error)
        throw error
      }
    }
  }
}

// Re-export types for convenience
export type {
  CommParticipant,
  CommData,
  ContactData,
  GetCommParams,
  GetCommResponse,
  UpdateCommPresenceParams,
  UpdateCommPresenceResponse
} 